<template>
  <div :class="classes" class="dropdown">
    <div ref="reference" class="input-wrap">
      <a class="input-icon">
        <i v-show="!disabled && !isClear" ref="referenceI"
           class="aid aid-calendar-select icon-calendar"
           @click="$_onSelectCalendarIconClick"></i>
        <i v-show="!disabled && isClear"
           :title="t('t.datepicker.clear')"
           class="icon-clear"
           @click.stop="$_onClearIconClick"></i>
      </a>
      <input ref="referenceInput"
             :value="selectDateValue"
             :disabled="disabled"
             :class="{'input--lg':size==='lg','input--sm':size==='sm'}" :placeholder="placeholder"
             type="text"
             readonly="readonly"
             class="input dropdown__toggle cursor-pointer"
             @blur="$_onInputBlur"
             @click="$_onSelectCalendarIconClick">
    </div>
    <t-pop-box v-clickoutside="$_onClickOutside" v-show="visible" ref="drop"
               :placement="placement"
               :class="[prefixDatePickClass, (isRange ? `${prefixDatePickClass}-range` : '')]">
      <div :class="`${prefixDatePickClass}-content`">
        <t-date-panel
          :visible="visible"
          :type="type"
          :show-time="showTime"
          :show-today="showToday"
          :is-range="isRange"
          :default-draw-date="drawDate"
          :min-selected="minSelected"
          :max-selected="maxSelected"
          :show-shortcut="showShortcut"
          :shortcut-options="shortcutOptions"
          :confirm="confirmCache"
          :min-select-range="minSelectRangeCache"
          :max-select-range="maxSelectRangeCache"
          :unselectable-days-of-week="unselectableDaysOfWeek"
          :unselectable-days="unselectableDays"
          @panel-pick="$_onPanelPick">
        </t-date-panel>
      </div>
    </t-pop-box>
  </div>
</template>
/**
 * 所有的date都是moment格式, 默认语言为中文
 */
<script>
import moment from 'moment'
import TDatePanel from './panel/date-panel.vue'
import clickoutside from '../../directives/clickoutside'
import locale from '../../mixins/locale'
import Emitter from '../../mixins/emitter'
import TPopBox from '../pop-box/pop-box.vue'
import {
  PREFIX_DATE_PICK_CLASS,
  PREFIX_DATE_PICKER_CLASS
} from './constants'

const FORMAT_MONTH = 'YYYY-MM'
const FORMAT_YEAR = 'YYYY'
const FORMAT_DATE = 'YYYY-MM-DD'
const FORMAT_TIME = 'HH:mm:ss'
const TIME_SEPARATOR = ' '
const RANGE_SEPARATOR = ' ~ '

export default {
  name: 'TDatePicker',
  components: {
    TDatePanel,
    TPopBox
  },
  directives: { clickoutside },
  mixins: [locale, Emitter],
  props: {
    alignRight: Boolean,
    type: {
      // 可能的取值为：month|monthRange|date|dateRange|dateTime|dateTimeRange|time
      type: String,
      default: 'date'
    },
    rangeSeparator: {
      type: String,
      default: RANGE_SEPARATOR
    },
    clearable: {
      type: Boolean,
      default: true
    },
    showToday: {
      type: Boolean,
      default: false
    },
    showShortcut: {
      type: Boolean,
      default: false
    },
    shortcutOptions: {
      type: Object,
      default() {
        return {}
      }
    },
    size: String, // 可能的取值为：large,small,normal(default)
    placeholder: {
      type: String,
      default: ' '
    },
    format: String,
    disabled: Boolean,
    value: {
      type: [String, Array],
      default: ''
    },
    confirm: {
      type: Boolean,
      default: true
    },
    minSelectRange: String,
    maxSelectRange: String,
    showConfirm: {
      type: Boolean,
      default: true
    },
    unselectableDaysOfWeek: {
      type: Array,
      default: () => []
    },
    unselectableDays: {
      type: Array,
      default: () => []
    }
  },
  data: function() {
    return {
      visible: false,
      yearFormat: FORMAT_YEAR,
      monthFormat: FORMAT_MONTH,
      dateFormat: FORMAT_DATE,
      timeFormat: FORMAT_TIME,
      timeSeparator: TIME_SEPARATOR,
      valueType: 'String', // value is string or array
      minValue: '',
      maxValue: '',
      currentValue: '',
      isButtonClick: false,
      prefixClass: PREFIX_DATE_PICKER_CLASS,
      prefixDatePickClass: PREFIX_DATE_PICK_CLASS
    }
  },
  computed: {
    isOnlyTimeType () {
      return /^time/i.test(this.type)
    },
    classes() {
      let s = `${PREFIX_DATE_PICKER_CLASS}`
      if (this.value) {
        s += ` ${PREFIX_DATE_PICKER_CLASS}--clear`
      }
      if (this.alignRight) {
        s += ' alignRight'
      }
      return s
    },
    placement() {
      if (this.alignRight) {
        return 'bottom-end'
      } else {
        return 'bottom-start'
      }
    },
    isClear() {
      if (this.clearable) {
        if (this.isRange === true) {
          return !!this.value.length
        } else {
          return !!this.value
        }
      }
      return false
    },
    selectDateValue() {
      return this.getSelectDateValue()
    },
    formatAll() {
      if (this.type === 'dateTime' || this.type === 'dateTimeRange') {
        return this.dateFormat + this.timeSeparator + this.timeFormat
      } else if (this.type === 'month' || this.type === 'monthRange') {
        return this.monthFormat
      } else if (this.type === 'year' || this.type === 'yearRange') {
        return this.yearFormat
      } else if (this.type === 'time' || this.type === 'timeRange') {
        return this.timeFormat
      } else {
        return this.dateFormat
      }
    },
    showTime() {
      if (this.type === 'dateTime' || this.type === 'dateTimeRange') {
        return true
      } else {
        return false
      }
    },
    isRange() {
      if (
        this.type === 'dateRange' ||
        this.type === 'dateTimeRange' ||
        this.type === 'monthRange'
      ) {
        return true
      } else {
        return false
      }
    },
    drawDate() {
      let minSelectRange = null
      if (this.minSelectRange) {
        minSelectRange = moment(this.minSelectRange, this.formatAll)
      }
      let maxSelectRange = null
      if (this.maxSelectRange) {
        maxSelectRange = moment(this.maxSelectRange, this.formatAll)
      }
      let date = moment(this.minValue || new Date(), this.formatAll)
      let drawDate = date
      if (minSelectRange && date.isBefore(minSelectRange)) {
        drawDate = minSelectRange
      }
      if (maxSelectRange && date.isAfter(maxSelectRange)) {
        drawDate = maxSelectRange
      }
      drawDate.set('hour', 0)
      drawDate.set('minute', 0)
      drawDate.set('second', 0)
      return drawDate
    },
    minSelected() {
      if (this.isOnlyTimeType === false) {
        if (!this.minValue) {
          return null
        }
        let newMoment = moment(this.minValue, this.formatAll)
        if (newMoment.isValid()) {
          return newMoment
        } else {
          throw new Error(this.t('t.datepicker.dataError'))
        }
      } else {
        let newMoment
        if (!this.minValue) {
          newMoment = moment()
          // 时间被清空后默认为当前时间
          let now = new Date()
          newMoment.hour(now.getHours()).minute(now.getMinutes()).second(now.getSeconds())
        } else {
          newMoment = moment(this.minValue, this.formatAll)
        }
        if (newMoment.isValid()) {
          return newMoment
        } else {
          throw new Error(this.t('t.datepicker.dataError'))
        }
      }
    },
    maxSelected() {
      if (!this.maxValue) {
        return null
      }
      let newMoment = moment(this.maxValue, this.formatAll)
      if (newMoment.isValid()) {
        return newMoment
      } else {
        throw new Error(this.t('t.datepicker.dataError'))
      }
    },
    minSelectRangeCache() {
      if (!this.minSelectRange) {
        return null
      }
      let newMoment = moment(this.minSelectRange, this.formatAll)
      if (newMoment.isValid()) {
        return newMoment
      } else {
        throw new Error(this.t('t.datepicker.dataError'))
      }
    },
    maxSelectRangeCache() {
      if (!this.maxSelectRange) {
        return null
      }
      let newMoment = moment(this.maxSelectRange, this.formatAll)
      if (newMoment.isValid()) {
        return newMoment
      } else {
        throw new Error(this.t('t.datepicker.dataError'))
      }
    },
    confirmCache() {
      if (
        this.type === 'date' ||
        this.type === 'dateRange' ||
        this.type === 'year' ||
        this.type === 'month'
      ) {
        return this.confirm
      } else {
        return true
      }
    }
  },
  watch: {
    selectDateValue(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.isRange === true) {
          if (!newVal) {
            newVal = []
          } else {
            newVal = newVal.split(this.rangeSeparator)
          }
        }
        this.publishDate(newVal)
      }
    },
    value(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setValue(newVal)
      }
    },
    visible(val) {
      if (val) {
        this.broadcast('TPopBox', 'on-update-popper')
      } else {
        this.broadcast('TPopBox', 'on-destroy-popper')
      }
    },
    minSelectRange(val) {
      // 如果当前选中的日期小于最小范围,就清空选中值
      if (this.value) {
        let value = Array.isArray(this.value) ? this.value[0] : this.value
        let formatedValue = moment(value, this.formatAll)
        let formatedMinSelectRange = moment(val, this.formatAll)
        if (formatedValue.isBefore(formatedMinSelectRange)) {
          this.clearDates()
        }
      }
    },
    maxSelectRange(val) {
      // 如果当前选中的日期大于最大范围,就清空选中值
      if (this.value) {
        let value = Array.isArray(this.value) ? this.value[1] : this.value
        let formatedValue = moment(value, this.formatAll)
        let formatedMaxSelectRange = moment(val, this.formatAll)
        if (formatedValue.isAfter(formatedMaxSelectRange)) {
          this.clearDates()
        }
      }
    }
  },
  created() {
    // 使用clickoutside指令处理
    // 创建事件：点击日历外的区域关闭日历
    if (Array.isArray(this.value)) {
      this.valueType = 'Array'
    } else {
      this.valueType = 'String'
    }
    if (!this.placeholder) {
      this.placeholder = this.t('t.datepicker.placeholder')
    }
    if (this.value) {
      this.setValue(this.value)
    }
  },
  methods: {
    getSelectDateValue() {
      if (this.isRange === false) {
        return this.minValue || ''
      } else {
        if (this.minValue && this.maxValue) {
          return this.minValue + this.rangeSeparator + this.maxValue
        } else {
          return ''
        }
      }
    },
    $_onInputBlur() {
      if (this.visible === false) {
        this.dispatch('TFormItem', 'on-form-blur', this.currentValue)
      }
    },
    $_onSelectCalendarIconClick() {
      if (!this.disabled) {
        this.visible = !this.visible
      }
      // 关闭其他日期控件的弹出窗 但是不能关闭自己控件的
      this.isButtonClick = this.visible
    },
    $_onClickOutside(e) {
      if (this.visible === true && this.isButtonClick === false) {
        this.visible = false
      }
      // 一次点击后还原
      if (this.isButtonClick === true) {
        this.isButtonClick = false
      }
    },
    $_onClearIconClick() {
      this.clearDates()
    },
    clearDates() {
      this.minValue = ''
      this.maxValue = ''
    },
    setValue(newVal) {
      if (this.isRange === true && !Array.isArray(newVal)) {
        throw new Error(this.t('t.datepicker.dataError'))
      }
      if (Array.isArray(newVal)) {
        this.valueType = 'Array'
        if (newVal.length) {
          this.minValue = newVal[0] || ''
          this.maxValue = newVal[1] || ''
        } else {
          this.minValue = ''
          this.maxValue = ''
        }
      } else {
        this.valueType = 'String'
        this.minValue = newVal || ''
      }
    },
    $_onPanelPick(selectDate, type) {
      this.setDate(selectDate, type)
    },
    setDate(selectDate, type) {
      // date is moment
      let _format = this.format || this.formatAll
      if (type) {
        if (selectDate && moment.isMoment(selectDate)) {
          this[type + 'Value'] = selectDate.format(_format)
        }
      } else {
        selectDate = Array.isArray(selectDate) ? selectDate : [selectDate]
        if (selectDate[0] && moment.isMoment(selectDate[0])) {
          this.minValue = selectDate[0].format(_format)
        } else {
          this.minValue = ''
        }
        if (selectDate[1] && moment.isMoment(selectDate[1])) {
          this.maxValue = selectDate[1].format(_format)
        } else {
          this.maxValue = ''
        }
      }

      // 如果是空的或者没有变化直接关闭
      if (
        !this.selectDateValue ||
        this.getSelectDateValue() === this.selectDateValue
      ) {
        this.visible = false
      }
    },
    // 通知外层选取的日期
    publishDate(value) {
      value = value || ''
      if (this.isRange === true && !value) {
        value = []
      }
      this.$emit('input', value)
      this.$emit('date-change', value)
      this.currentValue = value
      this.dispatch('TFormItem', 'on-form-change', value)
      this.visible = false
      this.dispatch('TFormItem', 'on-form-blur', this.currentValue)
    }
  }
}
</script>



// WEBPACK FOOTER //
// date-picker.vue?3e38e4bb